import { c as _c } from "react/compiler-runtime";
/* eslint-disable react/no-unstable-nested-components */
import type { DateInputFormat } from '@newfront-insurance/string-formatters';
import { CalendarDate, getLocalDateInputFormat, DateInputFormatPlaceholder } from '@newfront-insurance/string-formatters';
import kebabCase from 'lodash/kebabCase';
import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { useOnClickOutside } from '../../hooks/click-outside';
import { useKeyEvent } from '../../hooks/key-event';
import { Input } from '../../v2';
import { CalendarDatePicker } from '../calendar-date-picker';
import { IconDate } from '../icons';
import { ControlledPopper } from '../popper';
const EARLIEST_POSSIBLE_DATE = new CalendarDate('1900-01-01');
export interface CalendarDatePickerProps {
  autoComplete?: boolean;
  clearOnDisabled?: boolean;
  disabled?: boolean;
  disableWeekends?: boolean;
  fieldId?: string;
  isInvalid?: boolean;
  maxDate?: CalendarDate;
  minDate?: CalendarDate;
  name: string;
  onChange: (value?: CalendarDate) => unknown;
  testId?: string;
  value?: CalendarDate;
  aiSuggestionValue?: CalendarDate;
  format?: DateInputFormat;
  className?: string;
}
export function CalendarDatePickerInput(props) {
  const $ = _c(58);
  const {
    autoComplete: t0,
    clearOnDisabled,
    disableWeekends,
    disabled,
    value,
    isInvalid,
    maxDate,
    minDate: t1,
    name,
    onChange,
    fieldId,
    testId: propTestId,
    format: t2,
    aiSuggestionValue,
    className
  } = props;
  const autoComplete = t0 === undefined ? false : t0;
  const minDate = t1 === undefined ? EARLIEST_POSSIBLE_DATE : t1;
  let t3;
  if ($[0] !== t2) {
    t3 = t2 === undefined ? getLocalDateInputFormat() : t2;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const format = t3;
  let t4;
  if ($[2] !== fieldId || $[3] !== name || $[4] !== propTestId) {
    t4 = propTestId ?? kebabCase(fieldId ?? name);
    $[2] = fieldId;
    $[3] = name;
    $[4] = propTestId;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const testId = t4;
  const [isOpen, setVisible] = useState(false);
  const popoverRef = useRef(null);
  const triggerRef = useRef(null);
  let t5;
  if ($[6] !== isOpen) {
    t5 = e => {
      if (isOpen && e.target !== triggerRef.current) {
        setVisible(false);
      }
    };
    $[6] = isOpen;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  useOnClickOutside(popoverRef, t5);
  let t6;
  if ($[8] !== format || $[9] !== value) {
    t6 = () => formatValue(format, value);
    $[8] = format;
    $[9] = value;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const [inputValue, setInputValue] = useState(t6);
  let t7;
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setVisible(false);
    t8 = [];
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  useKeyEvent("Tab", t7, t8);
  let t10;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setVisible(false);
    t10 = [];
    $[13] = t10;
    $[14] = t9;
  } else {
    t10 = $[13];
    t9 = $[14];
  }
  useKeyEvent("Enter", t9, t10);
  let t11;
  if ($[15] !== disableWeekends || $[16] !== maxDate || $[17] !== minDate) {
    t11 = date => date.check({
      allowWeekends: !disableWeekends,
      maxDate,
      minDate
    });
    $[15] = disableWeekends;
    $[16] = maxDate;
    $[17] = minDate;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const isValidDate = t11;
  let t12;
  if ($[19] !== format || $[20] !== value) {
    t12 = () => {
      setInputValue(formatValue(format, value));
    };
    $[19] = format;
    $[20] = value;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const resetInputValue = t12;
  let t13;
  let t14;
  if ($[22] !== resetInputValue) {
    t13 = () => {
      resetInputValue();
    };
    t14 = [resetInputValue];
    $[22] = resetInputValue;
    $[23] = t13;
    $[24] = t14;
  } else {
    t13 = $[23];
    t14 = $[24];
  }
  useEffect(t13, t14);
  let t15;
  if ($[25] !== format || $[26] !== isValidDate || $[27] !== onChange || $[28] !== resetInputValue) {
    t15 = inputStr => {
      if (inputStr === "") {
        onChange(undefined);
        return;
      }
      const newDate = CalendarDate.fromString(inputStr ?? "", format);
      if (!newDate || !isValidDate(newDate)) {
        resetInputValue();
      } else {
        onChange(newDate);
      }
    };
    $[25] = format;
    $[26] = isValidDate;
    $[27] = onChange;
    $[28] = resetInputValue;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const onBlur = t15;
  let t16;
  if ($[30] !== aiSuggestionValue || $[31] !== value) {
    t16 = aiSuggestionValue && value?.isSameDate(aiSuggestionValue);
    $[30] = aiSuggestionValue;
    $[31] = value;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const withAiSuggestion = t16;
  let t17;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => setVisible(false);
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== autoComplete || $[35] !== className || $[36] !== clearOnDisabled || $[37] !== disabled || $[38] !== fieldId || $[39] !== format || $[40] !== inputValue || $[41] !== isInvalid || $[42] !== name || $[43] !== onBlur || $[44] !== testId || $[45] !== withAiSuggestion) {
    t18 = t19 => {
      const {
        triggerRef: currentTriggerRef
      } = t19;
      triggerRef.current = currentTriggerRef?.current;
      return <Input variant={withAiSuggestion ? "withAiSuggestion" : undefined} disabled={disabled} name={name} ref={currentTriggerRef} fieldId={fieldId} testId={testId} onChange={event => setInputValue(event.target.value)} onFocus={() => setVisible(true)} onKeyDown={_temp} onBlur={event_1 => onBlur(event_1.target.value)} placeholder={DateInputFormatPlaceholder[format]} type="text" value={clearOnDisabled && disabled ? "" : inputValue} autoComplete={autoComplete ? "on" : "off"} isInvalid={isInvalid} appendIcon={<button type="button" onClick={() => setVisible(true)} className="text-steel-400 dark:text-steel-300" tabIndex={-1}><IconDate color="currentColor" /></button>} wrapperClassName={className} />;
    };
    $[34] = autoComplete;
    $[35] = className;
    $[36] = clearOnDisabled;
    $[37] = disabled;
    $[38] = fieldId;
    $[39] = format;
    $[40] = inputValue;
    $[41] = isInvalid;
    $[42] = name;
    $[43] = onBlur;
    $[44] = testId;
    $[45] = withAiSuggestion;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== disabled || $[48] !== isValidDate || $[49] !== maxDate || $[50] !== minDate || $[51] !== onChange || $[52] !== value) {
    t19 = !disabled && <div ref={ref => {
      popoverRef.current = ref;
    }} className="p-2"><CalendarDatePicker value={value} validate={isValidDate} maxDate={maxDate} minDate={minDate} onChange={newDate_0 => {
        setVisible(false);
        if (newDate_0 && !isValidDate(newDate_0)) {
          return;
        }
        if (!value && !newDate_0) {
          return;
        }
        if (newDate_0 && value && value.isSameDate(newDate_0)) {
          return;
        }
        onChange(newDate_0);
      }} /></div>;
    $[47] = disabled;
    $[48] = isValidDate;
    $[49] = maxDate;
    $[50] = minDate;
    $[51] = onChange;
    $[52] = value;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== isOpen || $[55] !== t18 || $[56] !== t19) {
    t20 = <ControlledPopper offsetDistance={4} isOpen={isOpen} onToggle={t17} placement="bottom-start" trigger={t18}>{t19}</ControlledPopper>;
    $[54] = isOpen;
    $[55] = t18;
    $[56] = t19;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  return t20;
}
function _temp(event_0) {
  if (event_0.key === "Enter") {
    (event_0.target as HTMLInputElement).blur();
  }
}
function formatValue(format: DateInputFormat, date?: CalendarDate): string {
  if (date) {
    return date.inputFormat(format);
  }
  return '';
}